/**
 * O cockpit foi atualizado enquanto a aba estava aberta? (07/10)
 *
 * O cockpit é uma página só: o JavaScript carrega uma vez e fica na memória enquanto a aba
 * estiver aberta. Depois de um deploy, a API já é a nova e a tela continua a velha — e a
 * diferença não dá erro, só some coisa: em 07/10, na homologação, o agente mandou gráfico,
 * tabela e "como calculei", e a tela aberta desde antes do deploy não sabia desenhar nenhum
 * dos três. Na cliente é pior: o operador deixa a aba aberta de um dia para o outro, e o
 * deploy é fora do horário justamente por isso.
 *
 * A pergunta é barata e sem API nova: o `index.html` vem do nginx sem cache (`no-store`) e
 * aponta para o script de entrada do build, que tem o hash no nome. Se o que o servidor
 * aponta não é o que esta aba carregou, há versão nova. A cada cinco minutos e quando a aba
 * volta a ficar visível; só no build de produção (no Vite de desenvolvimento o script é o
 * fonte, e o recarregamento é dele).
 */
import { useEffect, useState } from "react";

export const INTERVALO_MS = 5 * 60_000;

/** O script de entrada que um `index.html` aponta (`/assets/index-<hash>.js`). */
export function scriptDeEntrada(html: string): string | null {
  for (const marca of html.matchAll(/<script\b[^>]*>/g)) {
    const etiqueta = marca[0];
    if (!/\btype="module"/.test(etiqueta)) continue;
    const src = /\bsrc="([^"]+)"/.exec(etiqueta);
    if (src?.[1]) return src[1];
  }
  return null;
}

/** O script de entrada que **esta aba** carregou, como caminho. */
export function scriptCarregado(documento: Document = document): string | null {
  const script = documento.querySelector<HTMLScriptElement>(
    'script[type="module"][src]',
  );
  if (!script) return null;
  return new URL(script.src, documento.baseURI).pathname;
}

export function useVersaoNova({
  ativo = import.meta.env.PROD,
  intervaloMs = INTERVALO_MS,
}: { ativo?: boolean; intervaloMs?: number } = {}): boolean {
  const [nova, definirNova] = useState(false);

  useEffect(() => {
    if (!ativo) return;
    const carregado = scriptCarregado();
    if (!carregado) return;
    let vivo = true;

    async function conferir() {
      try {
        const resposta = await fetch("/index.html", { cache: "no-store" });
        if (!resposta.ok) return;
        const servido = scriptDeEntrada(await resposta.text());
        if (vivo && servido && servido !== carregado) definirNova(true);
      } catch {
        // Sem rede agora: a próxima volta pergunta de novo.
      }
    }

    const relogio = window.setInterval(() => void conferir(), intervaloMs);
    const aoVoltar = () => {
      if (document.visibilityState === "visible") void conferir();
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      vivo = false;
      window.clearInterval(relogio);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [ativo, intervaloMs]);

  return nova;
}
